/* zd-countdown.js - the offer clock (Phase 2, SCR-02; round 2: rolling 2-hour default).
   Two modes, one theme setting (settings.zd_timer_mode, read from data-zd-timer on the element):
   - "rolling2h" (default, Mando's explicit choice Oct 5 2026): the offer ends at the next even 2-hour mark on the
     clock (00:00, 02:00, ... UTC epoch boundaries), then restarts. Every visitor sees the same number at the same
     moment; nothing is stored per visitor.
   - "weekly": the offer ends every Sunday at 11:59pm America/Los_Angeles (we count to 23:59:59). Once passed, the
     next Sunday is the target.
   The clock never shows a negative value or sits at 00:00:00. Pure maths take an injectable `now` (ms) so they are
   unit-tested (tests/countdown.spec.js) including both DST switches.
   Browser: window.ZDCountdown. Node: module.exports. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZDCountdown = api;
})(typeof window !== 'undefined' ? window : null, function () {
  var TZ = 'America/Los_Angeles';
  var DEFAULT_MODE = 'rolling2h';
  var ROLL_MS = 2 * 3600 * 1000;
  var fmtParts = null;
  function dtf() {
    if (!fmtParts) {
      fmtParts = new Intl.DateTimeFormat('en-US', {
        timeZone: TZ, hourCycle: 'h23', weekday: 'short',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
      });
    }
    return fmtParts;
  }
  var DAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  // Wall-clock parts of an instant in Los Angeles.
  function laParts(ms) {
    var o = {};
    dtf().formatToParts(new Date(ms)).forEach(function (p) { o[p.type] = p.value; });
    return { y: +o.year, m: +o.month, d: +o.day, h: +o.hour % 24, mi: +o.minute, s: +o.second, dow: DAYS[o.weekday] };
  }
  // UTC offset of Los Angeles at an instant, in ms (e.g. -7h during PDT).
  function offsetAt(ms) {
    var p = laParts(ms);
    var asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
    return asUtc - Math.floor(ms / 1000) * 1000;
  }
  // Instant of a Los Angeles wall-clock time (y, m 1-based, d, h, mi, s).
  function laToUtc(y, m, d, h, mi, s) {
    var guess = Date.UTC(y, m - 1, d, h, mi, s);
    var t = guess - offsetAt(guess);
    return guess - offsetAt(t); // second pass settles DST edges
  }
  // Next Sunday 23:59:59 LA strictly after `now`.
  function nextWeeklyEnd(now) {
    if (now === undefined) now = Date.now();
    var p = laParts(now);
    var add = (7 - p.dow) % 7;
    for (var i = 0; i < 3; i++) {
      var day = new Date(Date.UTC(p.y, p.m - 1, p.d + add + 7 * i));
      var end = laToUtc(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), 23, 59, 59);
      if (end > now) return end;
    }
    return now + 7 * 864e5;
  }
  // Next 2-hour boundary strictly after `now` (same instant for every visitor, at most 2h away).
  function nextRollingEnd(now, periodMs) {
    if (now === undefined) now = Date.now();
    periodMs = periodMs || ROLL_MS;
    return (Math.floor(now / periodMs) + 1) * periodMs;
  }
  function nextEnd(mode, now) {
    return mode === 'weekly' ? nextWeeklyEnd(now) : nextRollingEnd(now);
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  // "2d 04:05:06" (days only when > 0), never negative.
  function format(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(s / 86400); s %= 86400;
    var hms = pad(Math.floor(s / 3600)) + ':' + pad(Math.floor((s % 3600) / 60)) + ':' + pad(s % 60);
    return (d ? d + 'd ' : '') + hms;
  }
  // Writes the remaining time into `el` every second. opts.now: clock function (tests); opts.mode overrides the
  // element's data-zd-timer ("rolling2h" | "weekly").
  function mount(el, opts) {
    opts = opts || {};
    var clock = opts.now || Date.now;
    var mode = opts.mode || (el && el.getAttribute && el.getAttribute('data-zd-timer')) || DEFAULT_MODE;
    var end = nextEnd(mode, clock());
    function tick() {
      var n = clock();
      if (n >= end) end = nextEnd(mode, n);
      el.textContent = format(end - n);
      el.setAttribute('data-zd-end', new Date(end).toISOString());
      el.setAttribute('data-zd-mode', mode);
    }
    tick();
    return setInterval(tick, 1000);
  }
  return { TZ: TZ, DEFAULT_MODE: DEFAULT_MODE, ROLL_MS: ROLL_MS, laParts: laParts, laToUtc: laToUtc, nextWeeklyEnd: nextWeeklyEnd, nextRollingEnd: nextRollingEnd, nextEnd: nextEnd, format: format, mount: mount };
});
